import { logger } from '@tuic/logger';

import { type SPACE_NAME } from '../utils';
import {
  processEntries,
  processEntriesWithConfig,
} from '../utils/brand-name-processor';

import { getEntries, cachedGetEntries } from './entries';

import type {
  BrandNameProcessingConfig,
  ContentTypeConfig,
} from '../utils/brand-name-processor';
import type { EntryFromSkeleton } from './entries';
import type { Locale } from '@tuic/i18n';
import type { EntriesQueries, EntrySkeletonType } from 'contentful';

type BaseQuery = {
  locale: Locale;
  content_type: string;
};

type Query<Skeleton extends EntrySkeletonType> = BaseQuery &
  EntriesQueries<Skeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;

type EntriesInChunksQuery<Skeleton extends EntrySkeletonType> =
  Query<Skeleton> & {
    limit?: never;
    skip?: never;
  };

type GetEntriesInChunksArgs<Skeleton extends EntrySkeletonType> = {
  space: SPACE_NAME;
  query: EntriesInChunksQuery<Skeleton>;
  preview?: boolean;
  size?: number;
  resolveIdentifier?: (entry: EntryFromSkeleton<Skeleton>) => string;
  shouldFetchNext?: (entries: EntryFromSkeleton<Skeleton>[]) => boolean;
  brandNameProcessing?: BrandNameProcessingConfig | ContentTypeConfig<Skeleton>;
};

export const getEntriesInChunks = async <Skeleton extends EntrySkeletonType>(
  args: GetEntriesInChunksArgs<Skeleton>,
): Promise<EntryFromSkeleton<Skeleton>[] | []> => {
  return await getEntriesInChunksInternal(args, {
    cached: false,
  });
};

export const cachedGetEntriesInChunks = async <
  Skeleton extends EntrySkeletonType,
>(
  args: GetEntriesInChunksArgs<Skeleton>,
): Promise<EntryFromSkeleton<Skeleton>[] | []> => {
  return await getEntriesInChunksInternal(args, {
    cached: true,
  });
};

/**
 * Fetches entries from given space using given query.
 */
const getEntriesInChunksInternal = async <Skeleton extends EntrySkeletonType>(
  {
    space,
    query,
    preview,
    size = 50,
    resolveIdentifier: resolveIdentifierArg,
    shouldFetchNext = entries => {
      return entries.length === size;
    },
    brandNameProcessing,
  }: GetEntriesInChunksArgs<Skeleton>,
  internal: {
    cached: boolean;
  },
): Promise<EntryFromSkeleton<Skeleton>[] | []> => {
  const fetcher = internal.cached ? cachedGetEntries : getEntries;

  const resolveIdentifier = getIdentifierResolver(
    resolveIdentifierArg,
    internal.cached,
    query,
  );

  try {
    let idx = -1;
    const inCollection: string[] = [];
    const collection: EntryFromSkeleton<Skeleton>[] = [];
    let chunk: EntryFromSkeleton<Skeleton>[] = [];

    do {
      idx++;

      chunk = await fetcher<Skeleton>({
        space,
        query: {
          ...query,
          limit: size,
          skip: idx * size,
        },
        preview,
        brandNameProcessing,
      });

      chunk.forEach(entry => {
        const id = resolveIdentifier(entry);

        if (inCollection.includes(id)) {
          return;
        }

        inCollection.push(id);
        collection.push(entry);
      });
    } while (shouldFetchNext(chunk));

    // Process the final collection if brandNameProcessing is enabled
    if (!brandNameProcessing) {
      return collection;
    }

    // Use the typed version if it's a ContentTypeConfig
    if (typeof brandNameProcessing === 'object') {
      // TypeScript can't statically determine if this is a ContentTypeConfig<Skeleton>
      // but we maintain type safety through the processEntriesWithConfig function
      return processEntriesWithConfig<Skeleton, EntryFromSkeleton<Skeleton>>(
        collection,
        brandNameProcessing as ContentTypeConfig<Skeleton>,
      );
    }

    // Default case - use the standard processEntries function
    return processEntries(collection, brandNameProcessing);
  } catch (e) {
    logger.error(
      `[getEntriesInChunks]: Contentful request failed. Query ${JSON.stringify(query)}`,
    );
    logger.error(e);
  }

  return [];
};

const getIdentifierResolver = <Skeleton extends EntrySkeletonType>(
  potentialResolveIdentifier: GetEntriesInChunksArgs<Skeleton>['resolveIdentifier'],
  cached: boolean,
  query: EntriesInChunksQuery<Skeleton>,
): NonNullable<GetEntriesInChunksArgs<Skeleton>['resolveIdentifier']> => {
  if (typeof potentialResolveIdentifier !== 'undefined') {
    return potentialResolveIdentifier;
  }

  return entry => {
    const id = entry.sys.id.toString();

    if (id !== '') {
      return id;
    }

    const invoked = cached ? 'cachedGetEntriesInChunks' : `getEntriesInChunks`;

    logger.error(
      `Could not resolve entry to identifier. Provide a custom \`resolveIdentifier\` argument in \`${invoked}\`. Query was: ${JSON.stringify(query)}`,
    );

    return '';
  };
};
